import { ISample } from '../models/ichannel';
import { Geometry } from '../utils/geometry';
import { Scaler } from '../utils/scaler';
import { IIntersectionViewModel, IPoint, IPointerViewModel } from '../models/interfaces';
import { max, min } from 'd3-array';

export class IntersectionViewModel implements IIntersectionViewModel {
    // data members
    pointer: IPointerViewModel;
    position: IPoint;

    private unscaler: Scaler | null = null;

    // constructor
    constructor(pointer: IPointerViewModel) {
        this.pointer = pointer;

        this.update();

        this.position = {
            x: pointer.positionX,
            y: this.getIntersection(this.pointer.positionX).y,
        };
    }

    // properties

    private _positionX: number = 0;
    get positionX() {
        return this._positionX;
    }
    set positionX(positionX: number) {
        this._positionX = positionX;

        const intersect = this.getIntersection(positionX);
        this._positionY = intersect.y;

        this.position = {
            x: intersect.x,
            y: intersect.y,
        };
    }

    private _positionY: number = 0;
    get positionY() {
        return this._positionY;
    }

    // public functions

    getIntersection(x: number): { x: number; y: number } {
        x = this.unscaler?.onX(x) ?? x;

        const rightPoint1 = { x: 0, y: 0 };
        const rightPoint2 = { x: 0, y: 0 };

        const samples: Array<ISample> = this.pointer.trace.windowedSamples;

        for (let i = 1; i < samples.length; i++) {
            if (i >= x) {
                rightPoint1.x = i - 1;
                rightPoint1.y = samples[i - 1].value;
                rightPoint2.x = i;
                rightPoint2.y = samples[i].value;
                break;
            } else {
                // stroke bottom bug => to avoid NaN with pointer to the end of trace

                rightPoint1.x = samples.length - 2;
                rightPoint1.y = samples[samples.length - 2].value;
                rightPoint2.x = samples.length - 1;
                rightPoint2.y = samples[samples.length - 1].value;
            }
        }

        const scaledPoint1 = { x: x, y: 0 };
        const scaledPoint2 = { x: x, y: 100 };

        const intersection = Geometry.intersection(scaledPoint1, scaledPoint2, rightPoint1, rightPoint2);

        intersection.x = this.pointer.trace?.scaler?.onX(intersection.x) ?? intersection.x;
        intersection.y = this.pointer.trace?.scaler?.onY(intersection.y) ?? intersection.y;

        return intersection;
    }

    update() {
        const minSampleValue = min(this.pointer.channel.samples, (data) => data.value) ?? 0;
        const maxSampleValue = max(this.pointer.channel.samples, (data) => data.value) ?? 0;
        const leadWidth = this.pointer.layout?.context?.leadWidth ?? 0;
        const scaleSettingsX = {
            domain: { min: 0, max: leadWidth },
            range: { min: 0, max: this.pointer.trace.windowedSamples.length },
        };
        const scaleSettingsY = { domain: { min: 100, max: 0 }, range: { min: minSampleValue, max: maxSampleValue } };
        this.unscaler = new Scaler(scaleSettingsX, scaleSettingsY);
    }
}
